import type { CreateSowInput } from '@clockwise/clients/sows';
import { ApiError, isoDateString } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Controller, type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { sowsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { localTodayIso } from '@/lib/utils';

const SowFormSchema = z.object({
  name: z.string().min(1, 'Required').max(200),
  documentNo: z.string().max(120).optional().or(z.literal('')),
  effectiveFrom: isoDateString('YYYY-MM-DD'),
  effectiveTo: isoDateString('YYYY-MM-DD').optional().or(z.literal('')),
});

type SowFormValues = z.infer<typeof SowFormSchema>;

const FORM_FIELDS = ['name', 'documentNo', 'effectiveFrom', 'effectiveTo'] as const;
type SowFormField = (typeof FORM_FIELDS)[number];

function isSowFormField(field: string): field is SowFormField {
  return (FORM_FIELDS as readonly string[]).includes(field);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
}

// Minimal SOW create: name + dates + optional document number. The line editor
// lives on the SOW detail page — a fresh SOW starts with zero engagements.
export function SowFormSheet({ open, onOpenChange, customerId }: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const form = useForm<SowFormValues>({
    resolver: zodResolver(SowFormSchema),
    defaultValues: blankSowValues(),
  });

  useEffect(() => {
    if (open) form.reset(blankSowValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: async (values: SowFormValues) => {
      const payload: CreateSowInput = {
        customerId,
        name: values.name,
        documentNo: values.documentNo ? values.documentNo : null,
        effectiveFrom: values.effectiveFrom,
        effectiveTo: values.effectiveTo ? values.effectiveTo : null,
        engagements: [],
      };
      return sowsApi.create(payload);
    },
    onSuccess: (res) => {
      toast.success('Engagement created');
      queryClient.invalidateQueries({ queryKey: queryKeys.sows.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      onOpenChange(false);
      // Land on the detail page already in amend mode so the user can add
      // engagements without an extra click — a fresh SOW has none yet.
      navigate({
        to: '/customers/$id/engagements/$engagementId',
        params: { id: customerId, engagementId: res.data.id },
        search: { amend: '1' },
      });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        for (const fe of err.fields) {
          if (isSowFormField(fe.path)) {
            form.setError(fe.path, { message: fe.message });
          }
        }
        toast.error('Please fix the highlighted fields');
        return;
      }
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    },
  });

  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));
  const errors = form.formState.errors;

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Add engagement</SheetTitle>
            <SheetDescription>
              Create a new SOW for this customer. Add engagements after it’s created.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-4">
              <Field
                id="name"
                label="Name"
                placeholder="FY26 services"
                error={errors.name?.message}
                register={form.register('name')}
              />
              <Field
                id="documentNo"
                label="Document #"
                placeholder="Optional"
                hint="Unique per customer when set."
                error={errors.documentNo?.message}
                register={form.register('documentNo')}
              />
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  control={form.control}
                  name="effectiveFrom"
                  render={({ field }) => (
                    <DateField
                      id="effectiveFrom"
                      label="Effective from"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.effectiveFrom?.message}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="effectiveTo"
                  render={({ field }) => (
                    <DateField
                      id="effectiveTo"
                      label="Effective to"
                      placeholder="open"
                      hint="Optional."
                      clearable
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      error={errors.effectiveTo?.message}
                    />
                  )}
                />
              </div>
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : 'Create engagement'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

function blankSowValues(): SowFormValues {
  return {
    name: '',
    documentNo: '',
    effectiveFrom: localTodayIso(),
    effectiveTo: '',
  };
}

interface FieldProps {
  id: SowFormField;
  label: string;
  placeholder?: string;
  hint?: string;
  error?: string;
  register: ReturnType<UseFormReturn<SowFormValues>['register']>;
}

function Field({ id, label, placeholder, hint, error, register }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} placeholder={placeholder} aria-invalid={Boolean(error)} {...register} />
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

interface DateFieldProps {
  id: SowFormField;
  label: string;
  placeholder?: string;
  hint?: string;
  error?: string;
  clearable?: boolean;
  value: string;
  onChange: (value: string) => void;
}

function DateField({
  id,
  label,
  placeholder,
  hint,
  error,
  clearable,
  value,
  onChange,
}: DateFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <DatePicker
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        clearable={clearable}
        ariaInvalid={Boolean(error)}
      />
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
